import { useState } from "react";
import {
  Routes,
  Route,
  NavLink,
  Navigate,
  Link,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import type { Me } from "./types";
import { LogoMark } from "./Logo";
import { Icon } from "./components/Icon";
import { ThemeToggle } from "./components/ThemeToggle";
import { ToastProvider } from "./components/Toast";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Children } from "./pages/Children";
import { Order } from "./pages/Order";
import { SubmissionDetail } from "./pages/SubmissionDetail";
import { Menus } from "./pages/Menus";
import { Admin } from "./pages/Admin";
import { Settings } from "./pages/Settings";
import { About } from "./pages/About";
import { Privacy, Terms } from "./pages/Legal";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "./contact";

function UserMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const initial = (me.user.name || me.user.email)[0]?.toUpperCase() ?? "?";
  return (
    <div className="usermenu" onBlur={() => setTimeout(() => setOpen(false), 120)}>
      <button className="trigger" onClick={() => setOpen((o) => !o)}>
        {me.user.picture ? (
          <img src={me.user.picture} alt="" />
        ) : (
          <span className="avatar-fallback">{initial}</span>
        )}
        <span className="email">{me.user.email}</span>
      </button>
      {open && (
        <div className="menu-pop">
          <div className="who">
            {me.user.name && <div>{me.user.name}</div>}
            {me.user.email}
            {me.isAdmin && <div className="badge info" style={{ marginTop: 6 }}>admin</div>}
          </div>
          <a href={SUPPORT_MAILTO} onMouseDown={(e) => e.preventDefault()}>
            <Icon name="mail" size={16} /> Contact support
          </a>
          <button
            onMouseDown={async () => {
              await fetch("/auth/logout", { method: "POST" });
              location.href = "/";
            }}
          >
            <Icon name="arrow-left" size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function ProtectedLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const loc = useLocation();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.get<Me>("/me") });

  // Only the very first load shows "Loading"; once we've settled (even on an
  // error) don't flip back, or a background refetch would unmount the <About/>
  // fallback and make it remount-refetch in a loop.
  if (me.isPending && !me.isError)
    return <div className="center-pad">Loading…</div>;
  if (me.error instanceof ApiError && me.error.status === 401) return <About />;
  if (me.error)
    return <div className="center-pad">Error: {(me.error as Error).message}</div>;

  const profile = me.data!;
  const link = ({ isActive }: { isActive: boolean }) =>
    isActive ? "active" : undefined;

  return (
    <ToastProvider>
      <header className="site-header">
        <div className="inner">
          <Link to="/" className="brand" onClick={() => setNavOpen(false)}>
            <span className="mark">
              <LogoMark size={18} />
            </span>
            <span className="hide-sm">Caldwell School Lunch</span>
          </Link>

          <button
            className="icon-btn nav-toggle"
            aria-label="Menu"
            onClick={() => setNavOpen((o) => !o)}
          >
            <Icon name={navOpen ? "x" : "menu"} />
          </button>

          <nav
            className={`nav ${navOpen ? "open" : ""}`}
            onClick={() => setNavOpen(false)}
          >
            <NavLink to="/" end className={link}>
              Dashboard
            </NavLink>
            <NavLink to="/children" className={link}>
              Children
            </NavLink>
            {(profile.isAdmin || profile.orderAssist) && (
              <NavLink to="/order" className={link}>
                Order
              </NavLink>
            )}
            <NavLink to="/menus" className={link}>
              Menu
            </NavLink>
            {profile.isAdmin && (
              <NavLink to="/admin" className={link}>
                Usage
              </NavLink>
            )}
            <NavLink to="/settings" className={link}>
              Settings
            </NavLink>
          </nav>

          <div className="header-tools">
            <ThemeToggle />
            <UserMenu me={profile} />
          </div>
        </div>
      </header>

      <main className="app-main" key={loc.pathname}>
        <Outlet context={profile} />
      </main>

      <footer className="site-footer">
        <div className="inner">
          <span>Caldwell School Lunch</span>
          <span className="grow" />
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href={SUPPORT_MAILTO} title={SUPPORT_EMAIL}>
            Contact
          </a>
        </div>
      </footer>
    </ToastProvider>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/children" element={<Children />} />
        <Route path="/order" element={<Order />} />
        <Route path="/submissions/:id" element={<SubmissionDetail />} />
        <Route path="/menus" element={<Menus />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
